import { LitElement, html, css, nothing, svg } from "lit";
import { customElement, state } from "lit/decorators.js";

import * as store from "../state/store.js";
import { SignalWatcher } from "../state/signal-watcher.js";
import {
  changeLayerColor,
  deleteLayer,
  renameLayer,
  selectLayer,
  toggleLock,
  toggleVisibility,
} from "../ops/layers.js";
import { confirmDialog, openContextMenu, toggleSidebar, toggleSidebarPosition } from "../ops/ui.js";
import type { Layer } from "../state/types.js";
import { PALETTE } from "../utils/colors.js";
import { tooltip } from "../utils/tooltip.js";
import "./toolbar.js";

// Lucide-derived SVG paths, stroked with currentColor so hover/active states
// just work. LOCK_OPEN is an outlined padlock with a bent shackle;
// LOCK_CLOSED fills the padlock body so the locked state pops even at 18px.
const LOCK_OPEN = svg`<rect width="18" height="11" x="3" y="11" rx="2" ry="2" fill="none"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/>`;
const LOCK_CLOSED = svg`<rect width="18" height="11" x="3" y="11" rx="2" ry="2" fill="currentColor" stroke="currentColor"/><path d="M7 11V7a5 5 0 0 1 10 0v4" fill="none"/>`;
const EYE_OPEN = svg`<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>`;
const EYE_OFF = svg`<path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/>`;
const TRASH = svg`<path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>`;
// Pencil / edit icon — click on the row triggers inline rename. Replaces
// the old dblclick-to-rename gesture entirely.
const PENCIL = svg`<path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>`;
// Pin icon shown on the "0" row to indicate it's the DXF system default
// and can't be renamed / deleted / moved.
const PIN = svg`<path d="M12 17v5"/><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"/>`;
// Panel-left / panel-right glyphs: outer frame + inner divider indicating
// which side the sidebar is docked to. Shown in the layer-panel header as
// the "dock" toggle -- the icon depicts the target side (where clicking
// would move it), not the current side.
const PANEL_LEFT = svg`<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/>`;
const PANEL_RIGHT = svg`<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="15" y1="3" x2="15" y2="21"/>`;

// ------- color-space helpers (only used by the SV/hue picker below) -------
// Hue in [0,1]; saturation/value in [0,1]; RGB channels in [0,255].
type Hsv = { h: number; s: number; v: number };

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

function hsvToRgb({ h, s, v }: Hsv): [number, number, number] {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  let r = 0, g = 0, b = 0;
  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function rgbToHsv(r: number, g: number, b: number): Hsv {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  const v = max;
  const s = max === 0 ? 0 : d / max;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h /= 6;
  }
  return { h, s, v };
}

function hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace(/^#/, "");
  if (m.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(m)) return [0, 0, 0];
  return [
    Number.parseInt(m.slice(0, 2), 16),
    Number.parseInt(m.slice(2, 4), 16),
    Number.parseInt(m.slice(4, 6), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => n.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}

function hexToHsv(hex: string): Hsv {
  return rgbToHsv(...hexToRgb(hex));
}

function hsvToHex(hsv: Hsv): string {
  return rgbToHex(...hsvToRgb(hsv));
}

@customElement("layer-panel")
export class LayerPanel extends SignalWatcher(LitElement) {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 340px;
      min-width: 340px;
      background: var(--bg-secondary);
      border-right: 1px solid var(--border-color);
      overflow: hidden;
      color: var(--text-primary);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }

    :host([position="right"]) {
      border-right: none;
      border-left: 1px solid var(--border-color);
    }

    /* Sheet mode — bottom drawer over the canvas on mobile. Applied via
       class="sheet" from dxf-editor.ts on narrow viewports. Height caps
       at 65vh so the canvas stays visible above and the user can still
       orient themselves; content scrolls inside. Border-radius on top
       corners + the drag handle sell the "sheet" affordance. */
    :host(.sheet) {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      min-width: 0;
      max-height: 65vh;
      height: auto;
      background: var(--sheet-bg, var(--bg-secondary));
      border-right: none;
      border-left: none;
      border-top: 1px solid var(--border-color);
      border-radius: 16px 16px 0 0;
      box-shadow: 0 -8px 32px var(--shadow, rgba(0, 0, 0, 0.4));
      z-index: 40;
      padding-bottom: var(--safe-bottom, 0px);
      animation: sheet-in 220ms cubic-bezier(0.22, 1, 0.36, 1);
      backdrop-filter: blur(12px);
    }
    :host(.sheet.collapsed) {
      display: none;
    }
    @keyframes sheet-in {
      from { transform: translateY(100%); }
      to   { transform: translateY(0); }
    }

    /* Drag handle bar — visual affordance at the top of the sheet. Doesn't
       drive an actual drag gesture (a modal-height gesture would need more
       plumbing than adds mobile parity is worth); the topbar Layers icon
       toggles the sheet's visibility. */
    .sheet-handle {
      display: none;
      justify-content: center;
      padding: 8px 0 4px;
      cursor: pointer;
    }
    :host(.sheet) .sheet-handle {
      display: flex;
    }
    .sheet-handle::before {
      content: "";
      width: 44px;
      height: 4px;
      border-radius: 2px;
      background: var(--border-color);
      opacity: 0.9;
    }

    header {
      padding: 6px 14px;
      background: var(--bg-tertiary);
      border-bottom: 1px solid var(--border-color);
      display: flex;
      justify-content: space-between;
      align-items: center;
      line-height: 1.4;
    }

    header h2 {
      font-size: 14px;
      font-weight: 600;
      color: var(--text-white);
      margin: 0;
      letter-spacing: 0.02em;
    }

    header .badge {
      font-size: 11px;
      background: var(--accent);
      color: #fff;
      padding: 2px 9px;
      border-radius: 12px;
    }

    /* Group everything on the right side of the header (badge + dock
       toggle) so the title stays anchored left. */
    header .right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    header .dock-btn {
      background: none;
      border: none;
      color: var(--text-secondary);
      cursor: pointer;
      padding: 2px;
      border-radius: 3px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: color 0.15s, background 0.15s;
    }

    header .dock-btn:hover {
      color: var(--text-primary);
      background: var(--bg-hover);
    }

    header .dock-btn svg {
      width: 16px;
      height: 16px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 2px 0;
      flex: 1;
      overflow-y: auto;
    }

    ul::-webkit-scrollbar { width: 4px; }
    ul::-webkit-scrollbar-track { background: var(--scrollbar-track); }
    ul::-webkit-scrollbar-thumb { background: var(--scrollbar-thumb); border-radius: 2px; }

    li {
      display: flex;
      align-items: center;
      padding: 5px 10px;
      gap: 8px;
      cursor: pointer;
      border-left: 3px solid transparent;
      transition: background 0.1s;
      /* Keep text selection off so a mousedown on the name doesn't turn the
         row into a text-selection gesture. */
      user-select: none;
      -webkit-user-select: none;
    }

    li:hover { background: var(--bg-hover); }

    li.selected {
      background: var(--bg-selected);
      border-left-color: #3794ff;
    }

    li.multi-selected {
      background: var(--bg-multi-selected);
      border-left-color: #3794ff;
    }

    /* Locked layer: subtle tint + right-edge accent stripe so the state
       reads at a glance regardless of which lock icon Chrome decides to
       render at 18px. Position: relative + ::after avoids adding an extra
       DOM element per row. */
    li.locked {
      position: relative;
      background: color-mix(in oklab, var(--bg-primary) 92%, transparent);
    }
    li.locked::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 3px;
      background: var(--accent);
      opacity: 0.7;
    }
    /* Selected still wins for the row background — the locked tint only
       shows when the row isn't the current focus. */
    li.selected.locked,
    li.multi-selected.locked {
      background: var(--bg-selected);
    }

    /* System-default "0" row: pinned to the top with a subtle badge and a
       chained left stripe so users know it's not deletable / renameable. */
    li.system {
      background: color-mix(in oklab, var(--bg-primary) 96%, transparent);
    }
    li.system .system-pin {
      display: inline-flex;
      align-items: center;
      color: var(--text-secondary);
      opacity: 0.7;
    }
    li:not(.system) .system-pin { display: none; }

    .swatch-wrap {
      position: relative;
      display: inline-block;
      flex-shrink: 0;
    }

    .swatch {
      width: 16px;
      height: 16px;
      border-radius: 3px;
      border: 2px solid var(--border-color);
      cursor: pointer;
      display: block;
      box-sizing: border-box;
    }

    .swatch:hover { border-color: var(--accent); }

    /* Color palette popover. position:fixed so it escapes the UL's
       overflow-y:auto scroll container — otherwise rows near the bottom
       of the layer list get their popover clipped by the UL. Coordinates
       come from the anchor swatch's viewport rect, updated whenever the
       palette opens. */
    .palette {
      position: fixed;
      top: var(--palette-top, 0);
      left: var(--palette-left, 0);
      z-index: 3000;
      background: var(--bg-tertiary);
      border: 1px solid var(--border-color);
      border-radius: 6px;
      padding: 10px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      box-shadow: 0 6px 20px var(--shadow);
      width: 232px;
    }

    .palette .chips {
      display: grid;
      grid-template-columns: repeat(8, 22px);
      grid-auto-rows: 22px;
      gap: 6px;
      justify-content: center;
    }

    .palette .chip {
      width: 22px;
      height: 22px;
      box-sizing: border-box;
      border-radius: 4px;
      cursor: pointer;
      border: 1px solid rgba(0, 0, 0, 0.35);
      padding: 0;
      margin: 0;
      /* Kill UA button chrome so the inline background shows through. */
      appearance: none;
      -webkit-appearance: none;
      background-image: none;
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
      transition: transform 0.1s;
      display: block;
    }

    .palette .chip:hover {
      transform: scale(1.15);
      border-color: #fff;
    }

    .palette .chip.active {
      outline: 2px solid var(--accent);
      outline-offset: 1px;
    }

    /* Inline SV/hue picker below the palette chips — Untitled-UI style solid
       tab. Lives in the same popover so users can freely switch between a
       palette chip and a custom color without navigating away. */
    .picker {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding-top: 8px;
      border-top: 1px solid var(--border-color);
    }

    .sv-square {
      position: relative;
      width: 100%;
      height: 120px;
      border-radius: 4px;
      cursor: crosshair;
      touch-action: none;
      user-select: none;
      overflow: hidden;
      background-color: hsl(0, 100%, 50%);
      background-image:
        linear-gradient(to top, #000, transparent),
        linear-gradient(to right, #fff, transparent);
    }

    .sv-cursor {
      position: absolute;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 2px solid #fff;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.6);
      transform: translate(-50%, -50%);
      pointer-events: none;
    }

    .hue-slider {
      position: relative;
      width: 100%;
      height: 12px;
      border-radius: 6px;
      cursor: pointer;
      touch-action: none;
      user-select: none;
      background: linear-gradient(
        to right,
        hsl(0, 100%, 50%),
        hsl(60, 100%, 50%),
        hsl(120, 100%, 50%),
        hsl(180, 100%, 50%),
        hsl(240, 100%, 50%),
        hsl(300, 100%, 50%),
        hsl(360, 100%, 50%)
      );
    }

    .hue-cursor {
      position: absolute;
      top: 50%;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      border: 2px solid #fff;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.6);
      transform: translate(-50%, -50%);
      background: #ccc;
      pointer-events: none;
    }

    .hex-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .hex-preview {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      border: 1px solid var(--border-color);
      flex-shrink: 0;
    }

    .hex-input {
      flex: 1;
      background: var(--bg-primary);
      border: 1px solid var(--border-color);
      border-radius: 4px;
      color: var(--text-primary);
      padding: 4px 6px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 12px;
      min-width: 0;
    }

    .hex-input:focus {
      outline: 1px solid var(--accent);
      outline-offset: -1px;
    }

    .name-wrap {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
    .name {
      font-size: 13px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 2px 4px;
      border-radius: 3px;
    }
    .name.editing {
      /* Form controls do not inherit color from their shadow-DOM parent —
         they resolve their own UA styles, which land on black regardless
         of theme. Set the token explicitly so dark-mode edits read as
         light-on-dark. font: inherit picks up the row's 13/14px size so
         the text doesn't jump when the span-to-input flip happens. */
      background: var(--bg-primary);
      color: var(--text-primary);
      caret-color: var(--accent);
      font: inherit;
      outline: 1px solid var(--accent);
      border: none;
      cursor: text;
      overflow: visible;
      user-select: text;
      -webkit-user-select: text;
    }
    .name.editing::selection {
      background: color-mix(in oklab, var(--accent) 55%, transparent);
      color: var(--text-white);
    }
    .name.editing.invalid {
      outline-color: var(--danger, #e94b3c);
    }
    /* Inline validation feedback — shows under the input while the user is
       renaming and the current value fails validation. Slides in silently
       (no layout jump on the row's height because we only reserve height
       when .rename-error has content). */
    .rename-error {
      font-size: 11px;
      color: var(--danger, #e94b3c);
      line-height: 1.4;
      padding: 1px 4px 0;
    }

    .count {
      font-size: 11px;
      color: var(--text-secondary);
      min-width: 22px;
      text-align: right;
    }

    .icon-btn {
      background: none;
      border: none;
      color: var(--text-primary);
      cursor: pointer;
      padding: 2px 4px;
      border-radius: 3px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.15s, color 0.15s, background 0.15s;
    }

    .icon-btn svg {
      width: 16px;
      height: 16px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .icon-btn:hover { background: var(--bg-hover); }
    .icon-btn.delete:hover { color: var(--danger); }

    .visibility { opacity: 0.5; }
    .visibility:hover { opacity: 1; }
    .visibility.hidden { opacity: 0.25; color: var(--text-secondary); }

    /* Pencil / edit button: dim by default, full opacity on row hover so it
       stays discoverable without cluttering unhovered rows. Fully hidden on
       the "0" row (which can't be renamed). */
    .edit { opacity: 0; }
    li:hover .edit,
    li.selected .edit { opacity: 0.7; }
    .edit:hover { opacity: 1; color: var(--accent); }
    li.system .edit { display: none; }

    .lock { opacity: 0.5; }
    .lock:hover { opacity: 1; }
    .lock.on {
      opacity: 1;
      color: var(--accent);
    }

    .delete { opacity: 0.45; }
    li:hover .delete { opacity: 1; }

    .empty {
      padding: 20px;
      text-align: center;
      color: var(--text-secondary);
      font-size: 13px;
    }

    /* Mobile / bottom-sheet sizing. .sheet mode is the primary trigger — a
       narrow desktop window (< 720px) also picks up the taller row height so
       an accidental resize doesn't leave the panel with cramped tap targets. */
    :host(.sheet) li {
      padding: 10px 14px;
      min-height: 52px;
    }
    @media (max-width: 720px) {
      li {
        padding: 10px 14px;
        min-height: 52px;
      }
    }
    :host(.sheet) .edit,
    :host(.sheet) .delete,
    :host(.sheet) .visibility,
    :host(.sheet) .lock {
      opacity: 1;
    }
    :host(.sheet) .edit {
      opacity: 0.9;
    }
    :host(.sheet) .icon-btn {
      padding: 8px 10px;
      min-width: 40px;
      min-height: 40px;
    }
    :host(.sheet) .icon-btn svg {
      width: 20px;
      height: 20px;
    }
    :host(.sheet) header {
      padding: 4px 16px 8px;
    }
    :host(.sheet) header h2 {
      font-size: 15px;
    }
    :host(.sheet) ul {
      max-height: calc(65vh - 100px - var(--safe-bottom, 0px));
    }
    :host(.sheet) .name {
      font-size: 14px;
    }
    /* Locked-row accent stripe would visibly touch the sheet's rounded
       corner if it ran edge-to-edge; inset it a hair inside the round. */
    :host(.sheet) li.locked::after {
      top: 4px;
      bottom: 4px;
    }
  `;

  @state()
  private editingName: string | null = null;

  // Live-tracked draft value while the user is typing in the inline rename
  // input. Kept separate from the DOM's textContent so validation can react
  // on `@input` without hidden imperative writes. Reset to null between
  // edits so a stale draft from one rename cycle can't leak into the next.
  @state()
  private renameDraft: string | null = null;

  @state()
  private paletteOpenFor: string | null = null;

  // Viewport-relative anchor for the palette popover. Recomputed each time
  // the palette opens so it renders at the swatch that was clicked, not the
  // absolute origin of the layer panel.
  @state()
  private paletteAnchor: { top: number; left: number } | null = null;

  // Live HSV state for the SV+hue picker. Hue must be tracked separately from
  // the applied hex because at V=0 or S=0 the hex loses hue information, and
  // we still want the wheel to remember where the user was.
  @state()
  private pickerHsv: Hsv = { h: 0, s: 0, v: 0 };

  private draggingSv = false;
  private draggingHue = false;

  override connectedCallback(): void {
    super.connectedCallback();
    // Close the color palette when the user clicks anywhere outside a swatch.
    document.addEventListener("click", this.#onDocClick, true);
    document.addEventListener("keydown", this.#onDocKeydown);
    // Scroll/resize would leave a fixed-positioned popover stranded away
    // from its anchoring swatch, so close it instead of trying to reflow.
    window.addEventListener("resize", this.#closePalette);
    window.addEventListener("scroll", this.#closePalette, true);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("click", this.#onDocClick, true);
    document.removeEventListener("keydown", this.#onDocKeydown);
    window.removeEventListener("resize", this.#closePalette);
    window.removeEventListener("scroll", this.#closePalette, true);
  }

  override updated(): void {
    // Consume the pending-rename signal AFTER render committed the DOM.
    // #startEdit needs the row's <input> to exist in the shadow DOM so it
    // can focus + select it. Calling it in render would race the commit.
    const pending = store.pendingRenameLayer();
    if (pending != null && pending !== this.editingName) {
      // Clear the signal FIRST so the follow-up #startEdit → editingName
      // write doesn't re-trigger this handler in a loop.
      store.write.pendingRenameLayer(null);
      this.#startEdit(pending);
    }
  }

  #onDocKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this.paletteOpenFor !== null) {
      this.#closePalette();
    }
  };

  #closePalette = () => {
    if (this.paletteOpenFor !== null) {
      this.paletteOpenFor = null;
      this.paletteAnchor = null;
    }
  };

  override render() {
    const order = store.layerOrder();
    const layers = store.layers();
    const filter = store.filterText().toLowerCase();
    const selected = store.selectedLayer();
    const multi = store.selectedLayerNames();
    const visible = filter
      ? order.filter((n) => n.toLowerCase().includes(filter))
      : order;

    // Icon depicts the DESTINATION side (where a click would move the
    // panel), matching how "move to right" reads more naturally on a
    // left-docked panel than "currently on left".
    const dockedLeft = store.sidebarPosition() === "left";
    const dockIcon = dockedLeft ? PANEL_RIGHT : PANEL_LEFT;
    const dockTitle = dockedLeft ? "Dock to right" : "Dock to left";

    // Consume any pending rename request from the layer context menu (or
    // wherever else). Reading the signal registers the reactive dependency
    // so a future write triggers a re-render; the actual #startEdit runs
    // outside the render cycle in updated() below.
    void store.pendingRenameLayer();

    return html`
      <div class="sheet-handle" @click=${() => toggleSidebar()}></div>
      <header>
        <h2>Layers</h2>
        <span class="right">
          <button
            class="dock-btn"
            ${tooltip(dockTitle)}
            aria-label=${dockTitle}
            @click=${() => toggleSidebarPosition()}
          >
            <svg viewBox="0 0 24 24">${dockIcon}</svg>
          </button>
          <span class="badge">${order.length}</span>
        </span>
      </header>

      <layer-toolbar></layer-toolbar>

      <ul>
        ${visible.length === 0
          ? html`<li class="empty">
              ${filter ? "No layers match filter" : store.isLoaded() ? "No layers" : "No DXF loaded"}
            </li>`
          : visible.map((name) =>
              this.#renderItem(name, layers[name], selected === name, multi.has(name))
            )}
      </ul>
    `;
  }

  #renderItem(name: string, layer: Layer | undefined, isSelected: boolean, isMulti: boolean) {
    if (!layer) return nothing;
    const isEditing = this.editingName === name;
    const paletteOpen = this.paletteOpenFor === name;
    const isSystem = name === "0";
    const isLocked = layer.locked;
    const classes = [
      isSelected && "selected",
      isMulti && "multi-selected",
      isLocked && "locked",
      isSystem && "system",
    ]
      .filter(Boolean)
      .join(" ");

    // Live rename validation against the current draft (see renameDraft).
    const draft = isEditing ? this.renameDraft ?? name : null;
    const validationError = draft != null ? this.#validateRename(name, draft) : null;

    return html`
      <li
        class=${classes}
        data-name=${name}
        @click=${(e: MouseEvent) => {
          if (isEditing) return;
          selectLayer(name, { additive: e.ctrlKey || e.metaKey, range: e.shiftKey });
        }}
        @contextmenu=${(e: MouseEvent) => {
          if (isEditing) return;
          e.preventDefault();
          // preserveEntities: the user right-clicked to act on the current
          // selection via the layer menu — don't wipe it as a side effect of
          // targeting the menu at this layer row.
          selectLayer(name, { additive: false, range: false, preserveEntities: true });
          openContextMenu(e.clientX, e.clientY, name);
        }}
      >
        <span class="swatch-wrap" @click=${(e: Event) => e.stopPropagation()}>
          <span
            class="swatch"
            style="background: ${layer.color}"
            ${tooltip("Change layer color")}
            aria-label="Change layer color"
            @click=${(e: MouseEvent) => this.#togglePalette(name, e)}
          ></span>
          ${paletteOpen ? this.#renderPalette(name, layer.color) : nothing}
        </span>
        <div class="name-wrap">
          ${isEditing
            ? html`<input
                class="name editing ${validationError ? "invalid" : ""}"
                type="text"
                spellcheck="false"
                .value=${draft ?? name}
                @click=${(e: Event) => e.stopPropagation()}
                @mousedown=${(e: Event) => e.stopPropagation()}
                @input=${(e: InputEvent) => {
                  this.renameDraft = (e.currentTarget as HTMLInputElement).value;
                }}
                @keydown=${(e: KeyboardEvent) => this.#onRenameKeydown(e, name)}
                @blur=${() => this.#commitRename(name)}
              />`
            : html`<span
                class="name"
                ${tooltip("Click the pencil icon to rename")}
              >${name}</span>`}
          ${validationError
            ? html`<span class="rename-error">${validationError}</span>`
            : nothing}
        </div>
        <span class="count">${layer.entityIds.length}</span>
        ${isSystem
          ? html`<span
              class="system-pin"
              ${tooltip("System default layer — can't be renamed or deleted")}
              aria-label="System default layer"
            >
              <svg viewBox="0 0 24 24" width="14" height="14"
                stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" fill="none">${PIN}</svg>
            </span>`
          : nothing}
        ${!isSystem
          ? html`<button
              class="icon-btn edit"
              ${tooltip(isLocked
                ? "Unlock this layer to rename it"
                : "Rename layer")}
              aria-label="Rename layer"
              ?disabled=${isLocked || isEditing}
              @click=${(e: Event) => {
                e.stopPropagation();
                // Runtime freshness check — the closure's `isLocked` comes
                // from render, but the store is the source of truth on click.
                const live = store.layers()[name];
                if (!live || live.locked) return;
                this.#startEdit(name);
              }}
            >
              <svg viewBox="0 0 24 24">${PENCIL}</svg>
            </button>`
          : nothing}
        <button
          class="icon-btn visibility ${layer.visible ? "" : "hidden"}"
          ${tooltip(layer.visible ? "Hide layer" : "Show layer")}
          aria-label=${layer.visible ? "Hide layer" : "Show layer"}
          @click=${(e: Event) => {
            e.stopPropagation();
            toggleVisibility(name);
          }}
        >
          <svg viewBox="0 0 24 24">${layer.visible ? EYE_OPEN : EYE_OFF}</svg>
        </button>
        <button
          class="icon-btn lock ${isLocked ? "on" : ""}"
          ${tooltip(isLocked ? "Unlock layer (allow selection)" : "Lock layer (prevent selection)")}
          aria-label=${isLocked ? "Unlock layer" : "Lock layer"}
          @click=${(e: Event) => {
            e.stopPropagation();
            toggleLock(name);
          }}
        >
          <svg viewBox="0 0 24 24">${isLocked ? LOCK_CLOSED : LOCK_OPEN}</svg>
        </button>
        ${!isSystem && !isLocked
          ? html`<button
              class="icon-btn delete"
              ${tooltip("Delete Layer")}
              aria-label="Delete Layer"
              @click=${(e: Event) => {
                e.stopPropagation();
                // Belt-and-suspenders — the button is hidden when locked,
                // but a re-render race could theoretically deliver a click
                // to a stale closure. Re-read the store before acting.
                const live = store.layers()[name];
                if (!live || live.locked) return;
                confirmDialog({
                  title: `Delete layer "${name}"?`,
                  message: `Its ${live.entityIds.length} ${
                    live.entityIds.length === 1 ? "entity" : "entities"
                  } will be moved to layer "0". This can be undone with Ctrl+Z.`,
                  confirmLabel: "Delete",
                  danger: true,
                  onConfirm: () => deleteLayer(name),
                });
              }}
            >
              <svg viewBox="0 0 24 24">${TRASH}</svg>
            </button>`
          : nothing}
      </li>
    `;
  }

  // ---- color palette ----

  #renderPalette(name: string, currentColor: string) {
    const cur = currentColor.toLowerCase();
    const anchor = this.paletteAnchor;
    const styleVars = anchor
      ? `--palette-top: ${anchor.top}px; --palette-left: ${anchor.left}px;`
      : "";
    const hsv = this.pickerHsv;
    // Live hex derived from HSV — this is the "custom" value the user is
    // building, kept in sync with the sliders. The applied layer color is
    // whatever the store says, which may differ during a drag.
    const hex = hsvToHex(hsv);
    const hueDeg = Math.round(hsv.h * 360);
    const svBg = `hsl(${hueDeg}, 100%, 50%)`;
    return html`
      <div
        class="palette"
        style=${styleVars}
        @click=${(e: Event) => e.stopPropagation()}
      >
        <div class="chips">
          ${PALETTE.map(
            (c) => html`
              <button
                class="chip ${c.toLowerCase() === cur ? "active" : ""}"
                style=${`background-color: ${c};`}
                ${tooltip(c)}
                aria-label=${`Set color ${c}`}
                @click=${() => this.#applyChip(name, c)}
              ></button>
            `
          )}
        </div>
        ${this.#renderPicker(name, hex, svBg, hsv)}
      </div>
    `;
  }

  #renderPicker(name: string, hex: string, svBg: string, hsv: Hsv) {
    return html`
      <div class="picker">
        <div
          class="sv-square"
          style=${`background-color: ${svBg};`}
          @pointerdown=${(e: PointerEvent) => this.#onSvPointerDown(e, name)}
          @pointermove=${(e: PointerEvent) => this.#onSvPointerMove(e, name)}
          @pointerup=${this.#onSvPointerUp}
          @pointercancel=${this.#onSvPointerUp}
        >
          <div
            class="sv-cursor"
            style=${`left: ${hsv.s * 100}%; top: ${(1 - hsv.v) * 100}%;`}
          ></div>
        </div>
        <div
          class="hue-slider"
          @pointerdown=${(e: PointerEvent) => this.#onHuePointerDown(e, name)}
          @pointermove=${(e: PointerEvent) => this.#onHuePointerMove(e, name)}
          @pointerup=${this.#onHuePointerUp}
          @pointercancel=${this.#onHuePointerUp}
        >
          <div
            class="hue-cursor"
            style=${`left: ${hsv.h * 100}%; background-color: ${svBg};`}
          ></div>
        </div>
        <div class="hex-row">
          <span
            class="hex-preview"
            style=${`background-color: ${hex};`}
          ></span>
          <input
            class="hex-input"
            type="text"
            spellcheck="false"
            .value=${hex}
            @change=${(e: Event) => this.#onHexInput(e, name)}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
            }}
          />
        </div>
      </div>
    `;
  }

  #applyChip(name: string, color: string) {
    changeLayerColor(name, color);
    // Refresh the picker so the SV/hue cursors track the chip we just picked.
    this.pickerHsv = hexToHsv(color);
  }

  // ---- pointer-driven SV square + hue slider ----

  #onSvPointerDown = (e: PointerEvent, name: string) => {
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture?.(e.pointerId);
    this.draggingSv = true;
    this.#updateSvFromEvent(e, el, name);
  };

  #onSvPointerMove = (e: PointerEvent, name: string) => {
    if (!this.draggingSv) return;
    const el = e.currentTarget as HTMLElement;
    this.#updateSvFromEvent(e, el, name);
  };

  #onSvPointerUp = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    this.draggingSv = false;
  };

  #updateSvFromEvent = (e: PointerEvent, el: HTMLElement, name: string) => {
    const rect = el.getBoundingClientRect();
    const s = clamp01((e.clientX - rect.left) / rect.width);
    const v = clamp01(1 - (e.clientY - rect.top) / rect.height);
    const next = { h: this.pickerHsv.h, s, v };
    this.pickerHsv = next;
    changeLayerColor(name, hsvToHex(next));
  };

  #onHuePointerDown = (e: PointerEvent, name: string) => {
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture?.(e.pointerId);
    this.draggingHue = true;
    this.#updateHueFromEvent(e, el, name);
  };

  #onHuePointerMove = (e: PointerEvent, name: string) => {
    if (!this.draggingHue) return;
    const el = e.currentTarget as HTMLElement;
    this.#updateHueFromEvent(e, el, name);
  };

  #onHuePointerUp = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    this.draggingHue = false;
  };

  #updateHueFromEvent = (e: PointerEvent, el: HTMLElement, name: string) => {
    const rect = el.getBoundingClientRect();
    const h = clamp01((e.clientX - rect.left) / rect.width);
    const next = { h, s: this.pickerHsv.s, v: this.pickerHsv.v };
    this.pickerHsv = next;
    changeLayerColor(name, hsvToHex(next));
  };

  #onHexInput = (e: Event, name: string) => {
    const input = e.target as HTMLInputElement;
    const raw = input.value.trim().replace(/^#/, "");
    if (!/^[0-9a-fA-F]{6}$/.test(raw)) {
      // Invalid — snap back to whatever HSV currently reflects.
      input.value = hsvToHex(this.pickerHsv);
      return;
    }
    const hex = `#${raw.toUpperCase()}`;
    this.pickerHsv = hexToHsv(hex);
    changeLayerColor(name, hex);
  };

  #togglePalette = (name: string, e: MouseEvent) => {
    if (this.paletteOpenFor === name) {
      this.paletteOpenFor = null;
      this.paletteAnchor = null;
      return;
    }
    // Anchor the popover below the swatch that was clicked, in viewport coords.
    const swatch = e.currentTarget as HTMLElement;
    const rect = swatch.getBoundingClientRect();
    this.paletteAnchor = { top: rect.bottom + 6, left: rect.left };
    this.paletteOpenFor = name;
    const layer = store.layers()[name];
    if (layer) this.pickerHsv = hexToHsv(layer.color);
  };

  #onDocClick = (e: MouseEvent) => {
    if (this.paletteOpenFor === null) return;
    // If click is inside our shadow root and lands on a .swatch-wrap or the
    // fixed-positioned palette itself, keep the palette open. Otherwise close.
    const path = e.composedPath();
    for (const node of path) {
      if (!(node instanceof Element)) continue;
      if (node.classList?.contains("swatch-wrap")) return;
      if (node.classList?.contains("palette")) return;
    }
    this.paletteOpenFor = null;
    this.paletteAnchor = null;
  };

  // ---- inline rename ----

  // Entry point for every rename interaction: pencil-icon click, the
  // "Rename Layer" context-menu item (via pendingRenameLayer signal), or
  // any future keyboard shortcut. All roads land here so validation +
  // focus + draft-init happen once, consistently.
  #startEdit = (name: string) => {
    if (name === "0") return;
    const layer = store.layers()[name];
    if (!layer || layer.locked) return;
    this.editingName = name;
    this.renameDraft = name;
    this.paletteOpenFor = null;
    this.paletteAnchor = null;
    // Focus + select-all the input after Lit renders the <input>. `updated()`
    // could do this too but a microtask is enough — the render commit runs
    // synchronously under SignalWatcher inside the same task tick.
    queueMicrotask(() => {
      const el = this.renderRoot.querySelector<HTMLInputElement>(
        `li[data-name="${cssEscape(name)}"] input.name`
      );
      if (!el) return;
      el.focus();
      el.select();
    });
  };

  // Validation runs on every keystroke via renameDraft. Returns a user-
  // facing error string, or null when the draft is valid and would commit.
  // Kept in sync with `renameLayer`'s ops-level guards so the inline error
  // matches whatever the commit would toast.
  #validateRename(oldName: string, draft: string): string | null {
    const trimmed = draft.trim();
    if (!trimmed) return "Layer name can't be empty";
    if (trimmed === oldName) return null; // no-op, silently accepted
    const layers = store.layers();
    if (layers[trimmed]) return `"${trimmed}" already exists`;
    return null;
  }

  #onRenameKeydown = (e: KeyboardEvent, name: string) => {
    if (e.key === "Enter") {
      e.preventDefault();
      this.#commitRename(name);
    } else if (e.key === "Escape") {
      e.preventDefault();
      this.#cancelRename();
    }
  };

  #cancelRename(): void {
    this.editingName = null;
    this.renameDraft = null;
  }

  #commitRename(oldName: string): void {
    // Blur can fire twice (Enter → blur() + native blur), so guard against
    // re-entrancy by checking we're still the editing target.
    if (this.editingName !== oldName) return;
    const draft = (this.renameDraft ?? "").trim();
    // No change / empty: silently revert. Empty is caught by ops-level
    // renameLayer too, but early-exiting here keeps the toast quiet on
    // trivial commits.
    if (!draft || draft === oldName) {
      this.#cancelRename();
      return;
    }
    // Validation failed — bail without touching the store. The inline error
    // stays visible until the user edits or presses Esc.
    if (this.#validateRename(oldName, draft) !== null) {
      // Refocus so the user can fix it instead of dropping edit mode.
      queueMicrotask(() => {
        const el = this.renderRoot.querySelector<HTMLInputElement>(
          `li[data-name="${cssEscape(oldName)}"] input.name`
        );
        el?.focus();
      });
      return;
    }
    const ok = renameLayer(oldName, draft);
    // Whether it succeeded or fell through to a late toast, drop edit mode
    // — the panel re-renders the row from the store's fresh name.
    this.#cancelRename();
    void ok;
  }
}

// CSS.escape wrapper for building safe [data-name="..."] selectors.
function cssEscape(s: string): string {
  const anyWindow = window as unknown as { CSS?: { escape?: (v: string) => string } };
  if (anyWindow.CSS?.escape) return anyWindow.CSS.escape(s);
  return s.replace(/["\\]/g, "\\$&");
}

declare global {
  interface HTMLElementTagNameMap {
    "layer-panel": LayerPanel;
  }
}
